/**
 * QuestionCard — renders a question with quick-select option buttons.
 * Displayed below an AI message bubble when the agent includes a
 * `question` directive in its response.
 *
 * The user can press an option (sends it as a text message) or
 * type/speak freely instead.
 */

import React from 'react';
import { TouchableOpacity, View, StyleSheet } from 'react-native';
import { Text, VStack } from '@gluestack-ui/themed';
import type { ChatDirectives } from '../../types/conversation';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

interface QuestionCardProps {
  question: NonNullable<ChatDirectives['question']>;
  onSelectOption: (optionText: string) => void;
  disabled?: boolean;
}

const QuestionCard = React.memo(function QuestionCard({
  question,
  onSelectOption,
  disabled,
}: QuestionCardProps) {
  const { theme, fontScale, ffs } = useDisplayTheme();

  return (
    <VStack
      space="sm"
      style={[styles.container, { backgroundColor: theme.card, borderColor: theme.border }]}
      testID="chat-card-question"
      accessibilityLabel={question.text}
      accessibilityRole="text"
    >
      <Text
        style={[
          styles.questionText,
          {
            color: theme.text,
            fontSize: 15 * fontScale,
            fontWeight: '500',
            ...ffs,
          },
        ]}
      >
        {question.text}
      </Text>

      <View style={styles.optionsRow}>
        {question.options.map((option, index) => (
          <TouchableOpacity
            key={`${option}-${index}`}
            testID={`chat-option-${index}`}
            accessibilityLabel={option}
            accessibilityRole="button"
            onPress={() => onSelectOption(option)}
            disabled={disabled}
            style={[
              styles.optionButton,
              {
                backgroundColor: theme.background,
                borderColor: theme.primary,
              },
              disabled && styles.optionDisabled,
            ]}
          >
            <Text
              style={[
                styles.optionText,
                {
                  color: theme.primary,
                  fontSize: 14 * fontScale,
                  ...ffs,
                },
              ]}
            >
              {option}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </VStack>
  );
});

const styles = StyleSheet.create({
  container: {
    marginLeft: 40,
    marginRight: 16,
    marginTop: 4,
    marginBottom: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 16,
    borderWidth: 1,
  },
  questionText: {
    lineHeight: 20,
  },
  optionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
  },
  optionButton: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1.5,
  },
  optionDisabled: {
    opacity: 0.5,
  },
  optionText: {
    textAlign: 'center',
  },
});

export default QuestionCard;
